import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CreditCard, Building2, ArrowLeft, ShieldCheck, Truck } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const { items, total, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [bonificoSent, setBonificoSent] = useState(false);
  const [bonificoOrder, setBonificoOrder] = useState(null);
  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: '',
    notes: ''
  });
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      setForm(f => ({
        ...f,
        customer_name: f.customer_name || user.name || '',
        customer_email: f.customer_email || user.email || ''
      }));
    }
  }, [user]);

  if (items.length === 0 && !bonificoSent) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-6">
        <div className="w-24 h-24 rounded-full bg-[#FFF8F5] flex items-center justify-center mb-6">
          <span className="text-5xl">🛒</span>
        </div>
        <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">Carrello vuoto</h2>
        <p className="text-slate-500 mb-6">Aggiungi dei prodotti per procedere al checkout</p>
        <button
          onClick={() => navigate('/shop')}
          data-testid="checkout-goto-shop"
          className="px-6 py-3 bg-[#FF7043] text-white font-heading font-bold rounded-full btn-caribbean"
        >
          Vai allo Shop
        </button>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        items: items.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity,
          size: i.size
        })),
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        shipping_address: form.shipping_address,
        notes: form.notes || null,
        payment_method: paymentMethod
      };

      const orderRes = await axios.post(`${API}/orders`, orderData);
      const order = orderRes.data;

      if (paymentMethod === 'stripe') {
        const origin = window.location.origin;
        const sessionRes = await axios.post(`${API}/checkout/create-session`, {
          order_id: order.id,
          origin_url: origin
        });
        window.location.href = sessionRes.data.url;
      } else {
        setBonificoOrder(order);
        setBonificoSent(true);
        clearCart();
        toast.success('Ordine creato! Segui le istruzioni per il bonifico.');
      }
    } catch (err) {
      toast.error('Errore nel checkout. Riprova.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (bonificoSent && bonificoOrder) {
    return (
      <div className="min-h-[60vh] max-w-2xl mx-auto px-6 py-16">
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl shadow-slate-100/50 border border-slate-100 text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <ShieldCheck size={40} className="text-green-600" />
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-4">
            Ordine Ricevuto!
          </h2>
          <p className="text-slate-600 mb-8">
            Per completare l'acquisto, effettua un bonifico bancario con i seguenti dati:
          </p>
          <div className="bg-slate-50 rounded-2xl p-6 text-left space-y-3 mb-8">
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Banca</span>
              <span className="font-semibold text-slate-900">BPM</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Intestatario</span>
              <span className="font-semibold text-slate-900">A.S.D. Caribbean Blue Moon Dance School</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">IBAN</span>
              <span className="font-semibold text-slate-900 font-mono text-sm">IT82E0503414302000000011922</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Importo</span>
              <span className="font-bold text-[#FF7043] text-lg">€{bonificoOrder.total.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-slate-500">Causale</span>
              <span className="font-semibold text-slate-900">Ordine #{bonificoOrder.id.slice(0, 8)}</span>
            </div>
          </div>
          <p className="text-sm text-slate-500 mb-6">
            L'ordine sarà confermato solo dopo l'accredito dei fondi sul nostro conto. Per qualsiasi dubbio contattaci al <a href="tel:3801599564" className="text-[#0288D1] font-semibold">380 159 9564</a> o via email a <a href="mailto:caribbean.bluemoon2025@gmail.com" className="text-[#0288D1] font-semibold">caribbean.bluemoon2025@gmail.com</a>
          </p>
          <button
            onClick={() => navigate('/')}
            data-testid="bonifico-go-home"
            className="px-6 py-3 bg-[#0288D1] text-white font-heading font-bold rounded-full btn-caribbean btn-blue"
          >
            Torna alla Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-8 md:py-12">
        <button
          onClick={() => navigate(-1)}
          data-testid="checkout-back-btn"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
        >
          <ArrowLeft size={18} /> Indietro
        </button>

        <h1 className="font-heading text-3xl md:text-4xl font-black text-slate-900 mb-8">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Form */}
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Customer Info */}
              <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-lg shadow-slate-100/40">
                <h2 className="font-heading text-xl font-bold text-slate-900 mb-6">Dati di spedizione</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Nome completo *</Label>
                      <Input
                        id="name"
                        data-testid="checkout-name"
                        required
                        value={form.customer_name}
                        onChange={e => setForm(f => ({ ...f, customer_name: e.target.value }))}
                        placeholder="Mario Rossi"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Telefono *</Label>
                      <Input
                        id="phone"
                        data-testid="checkout-phone"
                        required
                        value={form.customer_phone}
                        onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))}
                        placeholder="380 123 4567"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      data-testid="checkout-email"
                      type="email"
                      required
                      value={form.customer_email}
                      onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))}
                      placeholder="email@esempio.it"
                    />
                  </div>
                  <div>
                    <Label htmlFor="address">Indirizzo di spedizione *</Label>
                    <Textarea
                      id="address"
                      data-testid="checkout-address"
                      required
                      value={form.shipping_address}
                      onChange={e => setForm(f => ({ ...f, shipping_address: e.target.value }))}
                      placeholder="Via Roma 1, 00100 Roma (RM)"
                      rows={2}
                    />
                  </div>
                  <div>
                    <Label htmlFor="notes">Note (opzionale)</Label>
                    <Input
                      id="notes"
                      data-testid="checkout-notes"
                      value={form.notes}
                      onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Citofono, piano..."
                    />
                  </div>
                </div>
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-lg shadow-slate-100/40">
                <h2 className="font-heading text-xl font-bold text-slate-900 mb-6">Metodo di pagamento</h2>
                <RadioGroup
                  value={paymentMethod}
                  onValueChange={setPaymentMethod}
                  className="space-y-3"
                  data-testid="payment-method-group"
                >
                  <label
                    className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                      paymentMethod === 'stripe'
                        ? 'border-[#FF7043] bg-[#FFF8F5]'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <RadioGroupItem value="stripe" data-testid="payment-stripe" />
                    <CreditCard size={24} className="text-[#FF7043]" />
                    <div>
                      <p className="font-heading font-bold text-slate-900">Carta di Credito / Postepay</p>
                      <p className="text-sm text-slate-500">Pagamento sicuro con Stripe</p>
                    </div>
                  </label>
                  <label
                    className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                      paymentMethod === 'bonifico'
                        ? 'border-[#0288D1] bg-blue-50/50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <RadioGroupItem value="bonifico" data-testid="payment-bonifico" />
                    <Building2 size={24} className="text-[#0288D1]" />
                    <div>
                      <p className="font-heading font-bold text-slate-900">Bonifico Bancario</p>
                      <p className="text-sm text-slate-500">L'ordine sarà confermato solo dopo l'accredito dei fondi sul nostro conto</p>
                    </div>
                  </label>
                </RadioGroup>
              </div>

              <div className="flex items-start gap-2 mb-4">
                <input type="checkbox" id="checkout-privacy" required className="mt-1 accent-[#FF7043]" data-testid="checkout-privacy-consent" />
                <label htmlFor="checkout-privacy" className="text-xs text-slate-500">
                  Accetto il trattamento dei miei dati personali in conformità alla <a href="/privacy" target="_blank" className="text-[#0288D1] underline">Privacy Policy</a> e ai <a href="/condizioni-vendita" target="_blank" className="text-[#0288D1] underline">Termini e Condizioni</a> per la gestione del mio ordine. *
                </label>
              </div>

              <button
                type="submit"
                data-testid="checkout-submit-btn"
                disabled={loading}
                className="w-full py-4 bg-[#FF7043] text-white font-heading font-bold text-lg rounded-full btn-caribbean disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? 'Elaborazione...' : (
                  paymentMethod === 'stripe'
                    ? 'Paga Ora'
                    : 'Conferma Ordine con Bonifico'
                )}
                {!loading && <ShieldCheck size={20} />}
              </button>
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-lg shadow-slate-100/40 sticky top-28">
              <h2 className="font-heading text-xl font-bold text-slate-900 mb-6">Riepilogo Ordine</h2>
              <div className="space-y-3 mb-6">
                {items.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <img src={item.image_url} alt={item.name} className="w-14 h-14 rounded-xl object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{item.name.replace(' Caribbean Blue Moon', '')}</p>
                      <p className="text-xs text-slate-500">
                        {item.size && `${item.size} · `}Qtà: {item.quantity}
                      </p>
                    </div>
                    <p className="font-heading font-bold text-sm text-slate-900">€{(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-slate-100 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Subtotale</span>
                  <span className="font-semibold">€{total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500 flex items-center gap-1"><Truck size={14} /> Spedizione</span>
                  <span className="font-semibold text-green-600">Da calcolare</span>
                </div>
                <div className="flex justify-between text-lg pt-2 border-t border-slate-100 mt-2">
                  <span className="font-heading font-bold text-slate-900">Totale</span>
                  <span className="font-heading font-bold text-[#FF7043]">€{total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
